import { IconButton, Tooltip } from '@material-ui/core';
import { Info, Lock } from '@material-ui/icons';
import { Inline, Snackbar, useResponsiveValue } from '@superdispatch/ui';
import { TextBox } from '@superdispatch/ui-lab';
import { useRef } from 'react';
import { CloudUploadIcon } from 'shared/icons/CloudUploadIcon';
import { PermissionAction } from 'shared/modules/permission/core/PermissionAction';
import { PermissionIcon } from 'shared/modules/permission/core/PermissionIcon';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { PermissionButton } from 'shared/modules/permission/core/PermissionButton';
import { LoadsImportErrorDialog } from './core/LoadsImportErrorDialog';
import { useLoadsImportFileInput } from './core/useLoadsImportFileInput';

interface LoadsImportButtonProps {
  onSubmitSuccess: () => void;
  onClick?: () => void;
}

export function LoadsImportButton({
  onSubmitSuccess,
  onClick,
}: LoadsImportButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: settings } = useCarrierSettings();
  const isMobile = useResponsiveValue(true, false);
  const {
    handleFileInputChange,
    inputMultiple,
    isSubmitting,
    legacyImportFiles,
    legacyImportErrorMessage,
    clearLegacyImportError,
  } = useLoadsImportFileInput({
    utmMedium: 'button',
    onImportSuccess: onSubmitSuccess,
  });

  const shouldRestrictImport = !settings?.can_import_load;

  return (
    <>
      {!!legacyImportFiles.length && (
        <LoadsImportErrorDialog
          files={legacyImportFiles}
          errorMessage={legacyImportErrorMessage}
          onClose={clearLegacyImportError}
        />
      )}

      <input
        value=""
        type="file"
        hidden={true}
        ref={inputRef}
        multiple={inputMultiple}
        accept=".pdf,.csv"
        onChange={handleFileInputChange}
      />

      {isMobile ? (
        <PermissionAction name="loads" permission="can_import">
          <IconButton
            disabled={isSubmitting}
            onClick={() => {
              if (!shouldRestrictImport) {
                inputRef.current?.click();
              } else {
                onClick?.();
              }
            }}
          >
            <PermissionIcon name="loads" permission="can_import">
              <CloudUploadIcon />
            </PermissionIcon>
          </IconButton>
        </PermissionAction>
      ) : (
        <>
          <Tooltip
            placement="bottom"
            title={
              shouldRestrictImport ? (
                <Inline verticalAlign="center">
                  <Lock />
                  <TextBox variant="body" color="white">
                    Upgrade to unlock this feature.
                  </TextBox>
                </Inline>
              ) : (
                ''
              )
            }
          >
            <PermissionButton
              name="loads"
              permission="can_import"
              startIcon={
                shouldRestrictImport ? <Lock color="action" /> : undefined
              }
              variant="neutral"
              disabled={isSubmitting}
              onClick={() => {
                if (!shouldRestrictImport) {
                  inputRef.current?.click();
                } else {
                  onClick?.();
                }
              }}
            >
              Import Load
            </PermissionButton>
          </Tooltip>

          <Tooltip
            enterTouchDelay={0}
            title="Open help article about PDF and CSV importing."
          >
            <IconButton
              size="small"
              target="_blank"
              rel="noopener noreferrer"
              href="https://support.superdispatch.com/hc/articles/35622600792339-Import-PDF-Dispatch-Sheets-CSV-Loads"
            >
              <Info color="primary" />
            </IconButton>
          </Tooltip>
        </>
      )}

      <Snackbar open={isSubmitting}>Importing dispatch sheet…</Snackbar>
    </>
  );
}
